import { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, Sparkles, Plus, ShoppingBag } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { ProductCard } from "../components/ProductCard";
import { CategoryFilter } from "../components/CategoryFilter";
import { AdSlotCard, injectAdSlots } from "../components/AdSlotCard";
import { api } from "../lib/api";
import { useI18n } from "../i18n";

const SALE_FILTERS = [
  { key: "all", label_key: "filter_all_sale" },
  { key: "wholesale", label_key: "filter_wholesale" },
  { key: "retail", label_key: "filter_retail" },
];

export default function ProductsBoardPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState("all");
  const [saleType, setSaleType] = useState("all");
  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [loading, setLoading] = useState(true);

  // sync URL ?q= into local state when it changes externally (e.g. header search)
  useEffect(() => {
    const urlQ = searchParams.get("q") || "";
    if (urlQ !== search) setSearch(urlQ);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const load = async () => {
    try {
      const res = await api.get("/products");
      setProducts(res.data);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const categoryCounts = useMemo(() => {
    const c = {};
    products.forEach((p) => {
      c[p.category] = (c[p.category] || 0) + 1;
    });
    return c;
  }, [products]);

  const filtered = useMemo(() => {
    return products.filter((p) => {
      if (category !== "all" && p.category !== category) return false;
      if (saleType !== "all") {
        if (p.sale_type !== saleType && p.sale_type !== "both") return false;
      }
      if (search) {
        const s = search.toLowerCase();
        if (
          !p.title.toLowerCase().includes(s) &&
          !p.description.toLowerCase().includes(s)
        )
          return false;
      }
      return true;
    });
  }, [products, category, saleType, search]);

  return (
    <div className="mx-auto max-w-[1400px] px-4 sm:px-6 md:px-10 py-6 sm:py-10 md:py-16">
      {/* Nav pill tabs */}
      <div className="flex flex-wrap items-center gap-2 mb-6 sm:mb-10">
        <button
          onClick={() => navigate("/")}
          data-testid="switch-to-requests"
          className="rounded-full border border-border bg-white px-4 sm:px-5 py-2 text-sm font-semibold hover:border-foreground/30 transition-colors"
        >
          {t("tab_requests")}
        </button>
        <button
          data-testid="switch-to-products"
          className="rounded-full bg-foreground text-background px-4 sm:px-5 py-2 text-sm font-semibold"
        >
          {t("tab_products")}
        </button>
      </div>

      {/* Hero */}
      <section className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 mb-10 lg:mb-20">
        <div className="lg:col-span-8">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-3 py-1 mb-6">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-accent"></span>
            </span>
            <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground">
              {t("hero_products_kicker")}
            </span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
            {t("hero_products_1")}
            <br />
            <span className="text-muted-foreground">{t("hero_products_2")}</span>
          </h1>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button
              data-testid="hero-list-product-btn"
              onClick={() => navigate("/new-product")}
              size="lg"
              className="rounded-md gap-2 h-12 px-6"
            >
              <Plus className="h-4 w-4" strokeWidth={2.5} />
              {t("nav_post_product")}
            </Button>
            <div className="flex items-center gap-6 pl-3">
              <div>
                <div className="font-display text-2xl font-bold tabular-nums">
                  {products.length}
                </div>
                <div className="text-[10px] tracking-[0.15em] uppercase font-semibold text-muted-foreground">
                  {t("stat_products")}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="lg:col-span-4 relative hidden lg:block">
          <div className="relative rounded-md border border-border bg-white p-6 overflow-hidden grain-overlay">
            <div className="flex items-start justify-between mb-6">
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground">
                Sample listing
              </div>
              <Sparkles className="h-4 w-4 text-accent" />
            </div>
            <div className="font-display text-2xl font-semibold leading-tight mb-2">
              Leather notebooks
              <br />
              hand-stitched
            </div>
            <div className="text-sm text-muted-foreground mb-6">
              A5, 200 pages, gift-ready
            </div>
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
              <div>
                <div className="text-[10px] tracking-[0.15em] uppercase font-semibold text-muted-foreground">
                  Price
                </div>
                <div className="font-mono-brand text-lg font-bold mt-0.5">$12</div>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.15em] uppercase font-semibold text-muted-foreground">
                  Type
                </div>
                <div className="text-sm font-semibold mt-0.5">Both</div>
              </div>
            </div>
            <div className="absolute -bottom-6 -right-6 h-32 w-32 rounded-full bg-accent/10 blur-2xl"></div>
          </div>
        </div>
      </section>

      {/* Main */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
        <div className="lg:col-span-3">
          <CategoryFilter
            selected={category}
            onSelect={setCategory}
            counts={categoryCounts}
          />
        </div>

        <div className="lg:col-span-9">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-3 flex-wrap">
              <ShoppingBag className="h-5 w-5" strokeWidth={2} />
              <h2 className="font-display text-2xl font-semibold tracking-tight">
                {filtered.length}{" "}
                <span className="text-muted-foreground font-normal">
                  {filtered.length === 1 ? "product" : "products"}
                </span>
              </h2>
              <div className="flex items-center gap-1 ml-1 rounded-full border border-border bg-white p-0.5">
                {SALE_FILTERS.map((f) => (
                  <button
                    key={f.key}
                    data-testid={`sale-filter-${f.key}`}
                    onClick={() => setSaleType(f.key)}
                    className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors duration-200 ${
                      saleType === f.key
                        ? "bg-foreground text-background"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {t(f.label_key)}
                  </button>
                ))}
              </div>
            </div>
            <div className="relative w-full sm:w-96">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                data-testid="products-search-input"
                value={search}
                onChange={(e) => {
                  const v = e.target.value;
                  setSearch(v);
                  if (v) setSearchParams({ q: v }, { replace: true });
                  else setSearchParams({}, { replace: true });
                }}
                placeholder="Search products by name, description…"
                className="pl-9 pr-3 rounded-full h-11 bg-white border-2 focus-visible:border-foreground/40"
              />
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className="animate-pulse rounded-md border border-border bg-white overflow-hidden"
                >
                  <div className="aspect-[4/3] bg-muted" />
                  <div className="p-5 space-y-3">
                    <div className="h-4 bg-muted rounded w-3/4" />
                    <div className="h-3 bg-muted rounded w-full" />
                  </div>
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div
              data-testid="products-empty-state"
              className="rounded-md border-2 border-dashed border-border bg-white p-12 md:p-16 text-center"
            >
              <div className="mx-auto h-14 w-14 rounded-full bg-secondary flex items-center justify-center mb-5">
                <ShoppingBag className="h-6 w-6 text-muted-foreground" strokeWidth={2} />
              </div>
              <h3 className="font-display text-xl font-semibold mb-2">
                {t("products_empty_title")}
              </h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto mb-6 leading-relaxed">
                {t("products_empty_desc")}
              </p>
              <Button
                data-testid="empty-list-product-btn"
                onClick={() => navigate("/new-product")}
                className="rounded-md gap-2"
              >
                <Plus className="h-4 w-4" />
                {t("products_empty_cta")}
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {injectAdSlots(filtered, 3).map((slot) =>
                slot.kind === "ad" ? (
                  <AdSlotCard key={slot.key} />
                ) : (
                  <ProductCard key={slot.it.id} product={slot.it} index={slot.index} />
                )
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
